import { Field, Input } from '@payaca/ambrosia';
import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';

import CheckboxField from '@payaca/components/checkboxField/CheckboxField';
import FeedbackMessage from '@payaca/components/feedbackMessage/FeedbackMessage';
import { AlertTooltip } from '@payaca/components/iconTooltip/IconTooltip';

import {
  useMaterial,
  useMaterialPurchaseIntent,
  useMaterialsListMaterial,
  useSupplier,
} from '@payaca/store/hooks/appState';
import { FeedbackLevel } from '@payaca/types/feedbackTypes';
import UntitledIcon from '@payaca/untitled-icons';

type Props = {
  materialPurchaseIntentId?: number;
  supplierId: number;
  quantitySelected?: number;
  materialsListMaterialId: number;
  onUnselect: () => void;
  onSelect: (materialQuantity: number) => void;
  defaultToQuantityEditMode?: boolean;
};
const MaterialPurchaseIntentSelectionControl: FC<Props> = ({
  materialPurchaseIntentId,
  supplierId,
  quantitySelected = 0,
  materialsListMaterialId,
  onUnselect,
  onSelect,
  defaultToQuantityEditMode = false,
}: PropsWithChildren<Props>): JSX.Element => {
  const [isInQuantityEditMode, setIsInQuantityEditMode] = useState(
    defaultToQuantityEditMode
  );

  const materialsListMaterial = useMaterialsListMaterial(
    materialsListMaterialId
  );
  const materialPurchaseIntent = useMaterialPurchaseIntent(
    materialPurchaseIntentId
  );

  const material = useMaterial(materialsListMaterial?.materialId);

  const maximumQuantity = useMemo(() => {
    if (!materialsListMaterial) return 0;

    return (
      materialsListMaterial.outstandingMaterialQuantity -
      materialsListMaterial.purchaseIntendedMaterialQuantity +
      (materialPurchaseIntent?.materialQuantity || 0)
    );
  }, [materialsListMaterial, materialPurchaseIntent?.materialQuantity]);

  useEffect(() => {
    if (quantitySelected && maximumQuantity !== quantitySelected) {
      setIsInQuantityEditMode(true);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'quantitySelected'
  }, [maximumQuantity]);

  useEffect(() => {
    if (defaultToQuantityEditMode) {
      setIsInQuantityEditMode(true);
    }
  }, [defaultToQuantityEditMode]);

  const preferredSupplierId = useMemo(() => {
    return material?.preferredSupplierId || material?.cheapestSupplierId;
  }, [material]);

  const preferredSupplier = useSupplier(preferredSupplierId);
  const supplier = useSupplier(supplierId);

  const isAvailableAtSupplier = useMemo(() => {
    return !!material?.supplierIds.includes(supplierId);
  }, [material, supplierId]);

  const tooltipMessage = useMemo(() => {
    if (isAvailableAtSupplier) {
      if (material?.preferredSupplierId) {
        if (material.preferredSupplierId !== supplierId) {
          return `${preferredSupplier?.name} is the preferred supplier`;
        }
      } else if (
        material?.cheapestSupplierId &&
        material.cheapestSupplierId !== supplierId
      ) {
        return `${preferredSupplier?.name} is the cheapest supplier`;
      }
    } else {
      return `This material is not linked to ${supplier?.name}`;
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'supplier.name'
  }, [isAvailableAtSupplier, preferredSupplier?.name, material, supplierId]);

  return (
    <div
      className={`material-purchase-intent-selection-control ${
        isAvailableAtSupplier ? 'available' : ''
      }`}
    >
      {!isInQuantityEditMode && (
        <CheckboxField
          name="isSelected"
          onChange={() =>
            quantitySelected ? onUnselect() : onSelect(maximumQuantity)
          }
          value={!!quantitySelected}
          label={
            <div className="material-purchase-intent-selection-label">
              <span className="material-name">
                <span>
                  {quantitySelected ? quantitySelected : maximumQuantity}
                </span>
                <UntitledIcon name="x.3" />
                <span>{material?.name}</span>
              </span>
              {tooltipMessage && <AlertTooltip tooltipText={tooltipMessage} />}
            </div>
          }
        />
      )}
      {isInQuantityEditMode && (
        <>
          <div className="quantity-edit-container">
            <Field.Legacy>
              <Input
                className="max-w-16"
                // @ts-expect-error `type="number"` is disallowed on Input — use NumberInput instead. Legacy usage.
                type="number"
                value={quantitySelected.toString()}
                onChange={(value) => {
                  const quantity = Math.max(0, parseInt(value));
                  if (quantity) {
                    onSelect(quantity);
                  } else {
                    onUnselect();
                  }
                }}
                min={0}
              />
            </Field.Legacy>
            <UntitledIcon name="x.3" />
            <span>{material?.name}</span>
          </div>
          <FeedbackMessage
            feedbackLevel={FeedbackLevel.INFORMATION}
            iconBefore="info-circle.3"
            message={`${maximumQuantity} outstanding on the materials list`}
          />
        </>
      )}
    </div>
  );
};

export default MaterialPurchaseIntentSelectionControl;
